import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Compass, Rocket, Play, GraduationCap, ArrowRight, Wand2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const FEATURES = [
  { icon: GraduationCap, title: "Virtual Lecture", desc: "An immersive guided lecture inside a themed 3D world.", color: "text-primary bg-primary/10" },
  { icon: Compass, title: "Virtual Tour", desc: "A guided field trip through explorable 3D environments.", color: "text-cyan-600 bg-cyan-500/10" },
  { icon: Rocket, title: "Immersive Experience", desc: "Hands-on simulations and role-play students can touch.", color: "text-accent bg-accent/10" },
];

const STEPS = [
  "Pick state, grade, subject & topic",
  "Choose from 3 AI lesson outlines",
  "Select an art style",
  "Review scripts, storyboards & quizzes",
  "Launch the 3D VR lesson",
];

const CAN_YOU = [
  "A volcano erupting from the inside?",
  "The signing of the Declaration of Independence?",
  "Inside a single human cell?",
  "Walking across the surface of Mars?",
  "A geometry proof you can step through?",
  "The floor of the Roman Senate?",
  "A rainforest canopy at sunrise?",
  "Ancient Egypt during the pyramids' build?",
];

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen">
      <header className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <div className="flex items-center">
          <img src="/logo-light.png" alt="VividVR" className="h-9 w-auto" />
        </div>
        <nav className="hidden lg:flex items-center gap-0.5">
          <button onClick={() => nav("/features")} className="px-4 py-2 rounded-full text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">Features</button>
          <button onClick={() => nav("/how-it-works")} className="px-4 py-2 rounded-full text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">How It Works</button>
          <button onClick={() => nav("/standards")} className="px-4 py-2 rounded-full text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">Standards</button>
          <button onClick={() => nav("/pricing")} className="px-4 py-2 rounded-full text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">Pricing</button>
          <button onClick={() => nav("/founding")} className="px-4 py-2 rounded-full text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">Founding Cohort</button>
        </nav>
        <div className="flex items-center gap-3">
          <Button variant="ghost" data-testid="landing-login-btn" onClick={() => nav(user ? "/dashboard" : "/login")}>
            {user ? "Dashboard" : "Sign in"}
          </Button>
          <Button data-testid="landing-getstarted-btn" onClick={() => nav(user ? "/generator" : "/register")} className="rounded-full">
            Get started <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="hero-noise">
        <div className="max-w-7xl mx-auto px-6 pt-14 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-[0.15em] mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Standards-aligned VR, built the night before
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight leading-[1.05]">
              Turn any topic into an{" "}
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">immersive VR lesson</span>{" "}
              in minutes.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Zero tech skills required. Enter your state, grade, subject and topic — VividVR generates the outlines, scripts,
              assessments, standards tags and a playable 3D world for your K-12 classroom.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" data-testid="hero-create-btn" onClick={() => nav(user ? "/generator" : "/register")} className="rounded-full text-base h-12 px-7">
                <Wand2 className="w-5 h-5 mr-2" /> Create a lesson
              </Button>
              <Button size="lg" variant="outline" onClick={() => nav(user ? "/dashboard" : "/login")} className="rounded-full text-base h-12 px-7">
                <Play className="w-5 h-5 mr-2" /> View dashboard
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span>✓ All 50 states</span>
              <span>✓ K-12 · every subject</span>
              <span>✓ Auto standards tagging</span>
            </div>
          </div>

          <div className="relative animate-in-up" style={{ animationDelay: "120ms" }}>
            <div className="rounded-3xl overflow-hidden shadow-2xl shadow-primary/20 border border-white/40 rotate-1">
              <img
                src="https://images.unsplash.com/photo-1771765780945-c788a6ce4b33?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxrMTIlMjBzdHVkZW50cyUyMHZpcnR1YWwlMjByZWFsaXR5JTIwY2xhc3Nyb29tJTIwbGVhcm5pbmd8ZW58MHx8fHwxNzg3NDU5ODI5fDA&ixlib=rb-4.1.0&q=85"
                alt="Students using VR in classroom"
                className="w-full h-[420px] object-cover"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 glass rounded-2xl px-5 py-4 shadow-xl -rotate-2">
              <div className="text-xs font-bold uppercase tracking-wider text-accent">Standard tagged</div>
              <div className="font-mono text-sm mt-1">NGSS.MS-ESS1-1</div>
            </div>
          </div>
        </div>
      </section>

      {/* Yes, we can put it in VR */}
      <section className="bg-foreground text-background" data-testid="yes-vr-section">
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-background text-xs font-bold uppercase tracking-[0.15em] mb-6">
              <Sparkles className="w-3.5 h-3.5" /> The question we always get
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight leading-[1.03]">
              "Can you put <span className="italic text-background/60">that</span> in VR?"
              <span className="block mt-3 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Yes. We can.</span>
            </h2>
            <p className="mt-6 text-lg text-background/70 max-w-xl leading-relaxed">
              Teachers ask us this all the time — and the answer hasn't changed once. If you can picture it,
              we can build it into an explorable 3D world, standards-tagged and ready before tomorrow's class.
              Where there's a will, there's a way.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" data-testid="yes-vr-cta" onClick={() => nav(user ? "/generator" : "/register")} className="rounded-full text-base h-12 px-7">
                <Wand2 className="w-5 h-5 mr-2" /> Try your toughest topic
              </Button>
              <Button size="lg" variant="outline" onClick={() => nav("/how-it-works")} className="rounded-full text-base h-12 px-7 bg-transparent border-white/25 text-background hover:bg-white/10 hover:text-background">
                See how it works
              </Button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {CAN_YOU.map((q, i) => (
              <div key={q} data-testid={`can-you-${i}`} className="flex items-start gap-3 rounded-2xl bg-white/[0.06] border border-white/10 px-4 py-3.5 backdrop-blur-sm">
                <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-primary" />
                </span>
                <span className="text-sm text-background/85 leading-snug">{q}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 lesson types */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-center">
          Three lesson formats, one click each
        </h2>
        <p className="text-center text-muted-foreground mt-3 max-w-2xl mx-auto">
          Every generation gives you three distinct outlines. Pick one, or regenerate three more.
        </p>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-2xl border border-border bg-card p-7 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${f.color}`}>
                <f.icon className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-display font-semibold mt-5">{f.title}</h3>
              <p className="text-muted-foreground mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-10 sm:p-14">
          <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight">From blank to VR in 5 steps</h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {STEPS.map((s, i) => (
              <div key={i} className="flex items-center gap-4 rounded-xl bg-white/5 border border-white/10 px-5 py-4">
                <div className="w-8 h-8 shrink-0 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center font-bold text-sm">
                  {i + 1}
                </div>
                <span className="font-medium">{s}</span>
              </div>
            ))}
          </div>
          <Button size="lg" data-testid="steps-cta-btn" onClick={() => nav(user ? "/generator" : "/register")} className="mt-9 rounded-full h-12 px-8 bg-white text-slate-900 hover:bg-slate-100">
            Start building free <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-border py-8 text-center text-sm text-muted-foreground">
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-3">
          <button onClick={() => nav("/features")} className="hover:text-foreground transition-colors">Features</button>
          <button onClick={() => nav("/how-it-works")} className="hover:text-foreground transition-colors">How It Works</button>
          <button onClick={() => nav("/standards")} className="hover:text-foreground transition-colors">Standards</button>
          <button onClick={() => nav("/pricing")} className="hover:text-foreground transition-colors">Pricing</button>
          <button onClick={() => nav("/founding")} className="hover:text-foreground transition-colors">Founding Cohort</button>
          <button onClick={() => nav("/pa-cyber-pilot")} className="hover:text-foreground transition-colors">PA Cyber Pilot</button>
        </div>
        VividVR · Immersive standards-aligned lessons for K-12 educators
      </footer>
    </div>
  );
}
